import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { APARTMENT_ZONES } from "../data/content";

export const Nav = ({ lang, setLang, t }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [aptOpen, setAptOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "history", label: t.nav.history },
    { id: "landmarks", label: t.nav.landmarks },
    { id: "market", label: t.nav.market },
    { id: "listings", label: t.nav.listings },
    { id: "living", label: t.nav.living },
    { id: "team", label: t.nav.team },
  ];

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const baseLink = scrolled ? "text-copou-muted" : "text-white/90";

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-copou-bg/85 border-b border-copou-line" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <a href="#top" data-testid="nav-logo" aria-label="Copou" className="flex items-baseline gap-2 group" />

        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`text-sm font-medium tracking-wide transition-colors hover:text-copou-primary ${baseLink}`}
            >
              {l.label}
            </button>
          ))}

          {/* Apartamente dropdown */}
          <div className="relative" onMouseEnter={() => setAptOpen(true)} onMouseLeave={() => setAptOpen(false)}>
            <button
              data-testid="nav-apartments-trigger"
              onClick={() => setAptOpen((v) => !v)}
              className={`inline-flex items-center gap-1 text-sm font-medium tracking-wide transition-colors hover:text-copou-primary ${baseLink}`}
            >
              {t.nav.apartments}
              <ChevronDown size={14} className={`transition-transform ${aptOpen ? "rotate-180" : ""}`} />
            </button>
            {aptOpen && (
              <div
                data-testid="nav-apartments-menu"
                className="absolute right-0 top-full pt-3 w-60"
              >
                <div className="bg-copou-bg border border-copou-line shadow-xl py-2">
                  {APARTMENT_ZONES.map((z, i) => (
                    <a
                      key={i}
                      href={z.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`apartments-zone-${i}`}
                      className="block px-5 py-2.5 text-sm text-copou-ink hover:bg-copou-surface hover:text-copou-primary transition-colors"
                    >
                      {z.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`flex items-center rounded-full border ${scrolled ? "border-copou-line" : "border-white/40"} overflow-hidden`}>
            {["ro", "en"].map((lng) => (
              <button
                key={lng}
                data-testid={`lang-toggle-${lng}`}
                onClick={() => setLang(lng)}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                  lang === lng
                    ? "bg-copou-primary text-white"
                    : scrolled ? "text-copou-muted hover:text-copou-primary" : "text-white/80 hover:text-white"
                }`}
              >
                {lng}
              </button>
            ))}
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`lg:hidden ${scrolled ? "text-copou-ink" : "text-white"}`}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-copou-bg border-t border-copou-line px-6 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left py-3 text-copou-ink border-b border-copou-line/60 font-medium"
            >
              {l.label}
            </button>
          ))}

          <div className="pt-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-copou-primary">{t.nav.apartments}</span>
            <div className="mt-2 flex flex-col">
              {APARTMENT_ZONES.map((z, i) => (
                <a
                  key={i}
                  href={z.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-apartments-zone-${i}`}
                  className="py-2.5 text-copou-ink border-b border-copou-line/60 hover:text-copou-primary transition-colors"
                >
                  {z.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
